import Bugsnag from '@bugsnag/js'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect } from 'react'
import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { Formtitle, Layout } from '../../components'
import { client } from '../../utils/client'
import { situation_before_contract, situation_before_contract_pro } from '../../utils/constants'
import { isApprenticeshipStepValid } from '../../utils/misc'
import { ApprenticeshipFormType } from '../../utils/sharedTypes'

type SituationPropsType = {
  form: ApprenticeshipFormType
  setForm: React.Dispatch<React.SetStateAction<ApprenticeshipFormType>>
}

const FixedMenuItem: React.FC<React.ComponentProps<typeof MenuItem>> = (props) => (
  <MenuItem style={{ whiteSpace: 'normal' }} {...props} />
)

const Situation = ({ form, setForm }: SituationPropsType) => {
  const navigate = useNavigate()
  const submitInfo = () => {
    client
      .patch(`/apprenticeship/public/${form.data.global_id}/submit/`, {
        ...form.data,
        last_diploma_title: form.data.last_diploma_title,
        last_diploma: form.data.last_diploma_name,
        last_diploma_prepared: form.data.last_diploma_prepared,
        former_salary: form.data.former_salary || null,
        former_weekly_work_duration: form.data.former_weekly_work_duration || null,
        is_former_apprenticeship: form.data.is_former_apprenticeship,
        aid_under_20: form.data.aid_under_20 === 'unknown' ? null : form.data.aid_under_20,
        boe_beneficiary: form.data.boe_beneficiary === 'unknown' ? null : form.data.boe_beneficiary,
      })
      .then((resp) => {
        if (resp.status === 200) {
          navigate({ pathname: './../confirmation/', search: location.search })
        }
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        console.error(err)
      })
  }

  const handleChange = (key: string, value: string | boolean | Record<string, string> | number | null) => {
    setForm((prevState) => {
      const errors = prevState.errors
      delete errors[key]

      switch (key) {
        case 'last_diploma_title':
          if (!value) {
            errors[key] = 'Ce champ est obligatoire'
          }
          break
      }

      return {
        ...prevState,
        data: {
          ...prevState.data,
          [key]: value,
        },
        errors: errors,
      }
    })
  }

  useEffect(() => {
    if (form.data.situation_before_contract === 'apprenticeship_contract') {
      handleChange('is_former_apprenticeship', true)
    } else if (form.data.situation_before_contract !== 'apprenticeship_contract') {
      handleChange('is_former_apprenticeship', false)
      handleChange('former_salary', null)
      handleChange('former_weekly_work_duration', null)
    }
  }, [form.data.situation_before_contract])

  return (
    <Layout form={form.data} steps={form.steps}>
      <Formtitle
        title="Votre situation"
        subtitle="Remplissez le formulaire ci-dessous pour poursuivre votre inscription"
      />

      <Grid container spacing={4}>
        {/* SECTION 1: STATUTS PARTICULIERS */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }} variant="outlined">
            <Typography variant="h6" mb={1} color="primary">
              Régime social et éventuels statuts particuliers
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Indiquez ci-dessous votre régime social et sélectionnez le ou les statuts qui vous concernent pour
              bénéficier d'aides spécifiques
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <FormControl fullWidth>
                  <InputLabel required id="social_regime">
                    Régime social
                  </InputLabel>
                  <Select
                    labelId="social_regime-label"
                    label="Régime social"
                    required
                    onChange={(e) => {
                      handleChange('social_regime', e.target.value)
                    }}
                    value={form.data.social_regime}
                  >
                    <FixedMenuItem value="unknown">Je ne sais pas</FixedMenuItem>
                    <FixedMenuItem value="msa">MSA</FixedMenuItem>
                    <FixedMenuItem value="urssaf">URSSAF</FixedMenuItem>
                  </Select>
                  <Typography variant="caption" mt={1}>
                    En général c'est URSSAF, sauf en cas d'alternance au sein d'une entreprise agricole où c'est MSA
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container alignItems="center">
                  <Checkbox
                    id="is_high_level_athlete"
                    sx={{ ml: -1 }}
                    checked={form.data.is_high_level_athlete ?? false}
                    onClick={() => {
                      handleChange('is_high_level_athlete', !form.data.is_high_level_athlete)
                    }}
                  />
                  <Typography>Sportif de haut niveau</Typography>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Grid container alignItems="center">
                  <Checkbox
                    id="is_disabled_worker"
                    sx={{ ml: -1 }}
                    checked={form.data.is_disabled_worker ?? false}
                    onClick={() => {
                      handleChange('is_disabled_worker', !form.data.is_disabled_worker)
                    }}
                  />
                  <Typography>Travailleur handicapé</Typography>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Grid container alignItems="center">
                  <Checkbox
                    id="want_to_create_enterprise"
                    sx={{ ml: -1 }}
                    checked={form.data.want_to_create_enterprise ?? false}
                    onClick={() => {
                      handleChange('want_to_create_enterprise', !form.data.want_to_create_enterprise)
                    }}
                  />
                  <Typography>Vous souhaitez créer une entreprise</Typography>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Grid container alignItems="center">
                  <Checkbox
                    id="none_of_the_above"
                    sx={{ ml: -1 }}
                    checked={form.data.none_of_the_above ?? false}
                    onClick={() => {
                      handleChange('none_of_the_above', !form.data.none_of_the_above)
                    }}
                  />
                  <Typography>Aucun des cas précédents</Typography>
                </Grid>
              </Grid>

              {!form.data.is_disabled_worker && (
                <Grid item xs={12}>
                  <Grid container alignItems="center">
                    <Checkbox
                      id="has_rqth_rights"
                      sx={{ ml: -1 }}
                      checked={form.data.has_rqth_rights ?? false}
                      onClick={() => {
                        handleChange('has_rqth_rights', !form.data.has_rqth_rights)
                      }}
                    />
                    <Typography sx={{ whiteSpace: 'normal' }} maxWidth={700}>
                      Vous pensez avoir des droits attachés à la Reconnaissance de qualité de travailleur handicapé
                      (RQTH) ?
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Grid>

            {/* Sous-section pour les travailleurs handicapés */}
            {!form.data.is_disabled_worker && form.data.has_rqth_rights && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h6" mb={2} color="secondary">
                  Informations complémentaires pour travailleur handicapé
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body1" fontWeight="bold" mb={1}>
                      Êtes-vous éligible à une équivalence jeunes (moins de 20 ans) ?
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel id="aid_under_20">Aide de l'emploi pour les jeunes de moins de 20 ans</InputLabel>
                      <Select
                        labelId="aid_under_20-label"
                        label="Aide de l'emploi pour les jeunes de moins de 20 ans"
                        onChange={(e) => {
                          if (!e.target.value) {
                            handleChange('aid_under_20', '')
                            return
                          }
                          handleChange('aid_under_20', e.target.value)
                        }}
                        value={form.data.aid_under_20}
                      >
                        <FixedMenuItem value="unknown">Je ne sais pas</FixedMenuItem>
                        <FixedMenuItem value="PCH">Prestation de Compensation du Handicap</FixedMenuItem>
                        <FixedMenuItem value="AEEH">Allocation d'Education de l'Enfant Handicapé</FixedMenuItem>
                        <FixedMenuItem value="PPS">Projet Personnalisé de Scolarisation</FixedMenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body1" fontWeight="bold" mb={1}>
                      Êtes-vous bénéficiaire de l'obligation d'emploi (BOE) ?
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel id="boe_beneficiary">Bénéficiaire d'une bourse d'obligation d'emploi</InputLabel>
                      <Select
                        labelId="boe_beneficiary-label"
                        label="Bénéficiaire d'une bourse d'obligation d'emploi"
                        onChange={(e) => {
                          if (!e.target.value) {
                            handleChange('boe_beneficiary', '')
                            return
                          }
                          handleChange('boe_beneficiary', e.target.value)
                        }}
                        value={form.data.boe_beneficiary}
                      >
                        <FixedMenuItem value="unknown">Je ne sais pas</FixedMenuItem>
                        <FixedMenuItem value="PCH">Prestation de Compensation du Handicap</FixedMenuItem>
                        <FixedMenuItem value="AEEH">Allocation d'Éducation de l'Enfant Handicapé</FixedMenuItem>
                        <FixedMenuItem value="PPS">Projet Personnalisé de Scolarisation</FixedMenuItem>
                        <FixedMenuItem value="ACCIDENT_TRAVAIL">
                          Accident du travail ou maladie professionnelle (≥ 10% d'incapacité)
                        </FixedMenuItem>
                        <FixedMenuItem value="PENSION_INVALIDITE">
                          Pension d'invalidité (réduction de capacité ≥ 2/3)
                        </FixedMenuItem>
                        <FixedMenuItem value="MILITAIRE_INVALIDITE">
                          Ancien militaire avec pension d'invalidité
                        </FixedMenuItem>
                        <FixedMenuItem value="SAPEUR_POMPIER">
                          Sapeur-pompier volontaire avec rente d'invalidité
                        </FixedMenuItem>
                        <FixedMenuItem value="VICTIME_TERRORISME">Victime d'un acte de terrorisme</FixedMenuItem>
                        <FixedMenuItem value="CMI_INVALIDITE">
                          Carte mobilité inclusion (CMI) mention invalidité
                        </FixedMenuItem>
                        <FixedMenuItem value="AAH">Allocation aux adultes handicapés (AAH)</FixedMenuItem>
                        <FixedMenuItem value="ATI">Allocation temporaire d'invalidité (ATI)</FixedMenuItem>
                        <FixedMenuItem value="RECLASSEMENT">Fonctionnaire/contractuel en reclassement</FixedMenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* SECTION 2: SITUATION PROFESSIONNELLE */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }} variant="outlined">
            <Typography variant="h6" mb={3} color="primary">
              Votre situation professionnelle actuelle
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Sélectionner les options qui décrivent au mieux votre situation professionnelle actuelle
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel required id="situation_before_contract">
                    Situation actuelle
                  </InputLabel>
                  <Select
                    required
                    labelId="situation_before_contract-label"
                    label="Situation actuelle"
                    onChange={(e) => {
                      handleChange('situation_before_contract', e.target.value)
                    }}
                    value={form.data.situation_before_contract}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          width: 0,
                        },
                      },
                    }}
                  >
                    {(form.data.apprenticeship_type === 'ContratPro'
                      ? situation_before_contract_pro
                      : situation_before_contract
                    ).map((situation) => (
                      <FixedMenuItem key={situation.id} value={situation.id}>
                        {situation.label}
                      </FixedMenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Grid container alignItems="center">
                  <Checkbox
                    id="is_registered_to_pole_emploi"
                    sx={{ ml: -1 }}
                    checked={form.data.is_registered_to_pole_emploi ?? false}
                    onClick={() => {
                      handleChange('is_registered_to_pole_emploi', !form.data.is_registered_to_pole_emploi)
                    }}
                  />
                  <Typography>Je suis inscrit à France Travail (anciennement Pôle Emploi)</Typography>
                </Grid>
              </Grid>

              {form.data.is_registered_to_pole_emploi && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Numéro d'inscription Pôle Emploi"
                      variant="outlined"
                      id="pole_emploi_registration_number"
                      required
                      value={form.data.pole_emploi_registration_number}
                      onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                        handleChange('pole_emploi_registration_number', e?.target.value)
                      }}
                      error={Boolean(form.errors.pole_emploi_registration_number)}
                      helperText={form.errors.pole_emploi_registration_number}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Durée d'inscription (en mois)"
                      variant="outlined"
                      id="pole_emploi_duration"
                      required
                      value={form.data.pole_emploi_duration}
                      onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                        handleChange('pole_emploi_duration', e?.target.value)
                      }}
                      error={Boolean(form.errors.pole_emploi_duration)}
                      helperText={form.errors.pole_emploi_duration}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Grid container alignItems="center">
                  <Checkbox
                    id="is_former_apprenticeship"
                    sx={{ ml: -1 }}
                    checked={form.data.is_former_apprenticeship ? true : false}
                    disabled={form.data.situation_before_contract === 'apprenticeship_contract'}
                    onClick={() => {
                      handleChange('is_former_apprenticeship', !form.data.is_former_apprenticeship)
                    }}
                  />
                  <Typography>J'ai déjà effectué un apprentissage</Typography>
                </Grid>
              </Grid>

              {form.data.is_former_apprenticeship && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      required={form.data.is_former_apprenticeship}
                      label="Dernier salaire brut (€)"
                      variant="outlined"
                      id="former_salary"
                      value={form.data.former_salary ?? undefined}
                      onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                        handleChange('former_salary', parseInt(e?.target.value))
                      }}
                      error={Boolean(form.errors.former_salary)}
                      helperText={form.errors.former_salary}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Durée hebdomadaire de travail (heures)"
                      variant="outlined"
                      id="former_weekly_work_duration"
                      required={form.data.is_former_apprenticeship}
                      value={form.data.former_weekly_work_duration}
                      onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                        handleChange('former_weekly_work_duration', parseInt(e?.target.value.replace(/[^0-9]/g, '0')))
                      }}
                      error={Boolean(form.errors.former_weekly_work_duration)}
                      helperText={form.errors.former_weekly_work_duration}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Si vous avez déjà fait une alternance, merci de renseigner votre dernier salaire brut et votre
                      durée hebdomadaire de travail
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* SECTION 3: PARCOURS DE FORMATION */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" mb={3} color="primary">
              Votre parcours de formation
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Renseignez votre parcours de formation et vos diplômes
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel required id="last_student_year_outcome">
                    Dernière année suivie en tant qu'étudiant
                  </InputLabel>
                  <Select
                    required
                    labelId="last_student_year_outcome-label"
                    label="Dernière année suivie en tant qu'étudiant"
                    onChange={(e) => {
                      handleChange('last_student_year_outcome', e.target.value)
                    }}
                    value={form.data.last_student_year_outcome}
                  >
                    {form.last_student_year_outcomes.map((outcome) => (
                      <FixedMenuItem key={outcome.id} value={outcome.id}>
                        {outcome.name}
                      </FixedMenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="last_diploma_prepared-label" required>
                    Dernier diplôme <b>préparé</b>
                  </InputLabel>
                  <Select
                    required
                    labelId="last_diploma_prepared-label"
                    label="Dernier diplôme préparé"
                    onChange={(e) => {
                      handleChange('last_diploma_prepared', e.target.value)
                    }}
                    value={form.data.last_diploma_prepared}
                  >
                    {form.diplomas.map((diploma) => (
                      <FixedMenuItem key={diploma.id} value={diploma.id}>
                        {diploma.name}
                      </FixedMenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Intitulé précis du dernier diplôme ou titre préparé"
                  id="last_diploma_title"
                  variant="outlined"
                  required
                  value={form.data.last_diploma_title}
                  onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                    handleChange('last_diploma_title', e?.target.value)
                  }}
                  error={Boolean(form.errors.last_diploma_title)}
                  helperText={form.errors.last_diploma_title}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel required id="last_diploma-label">
                    Dernier diplôme <b>obtenu</b>
                  </InputLabel>
                  <Select
                    required
                    labelId="last_diploma-label"
                    label="Dernier diplôme obtenu"
                    onChange={(e) => {
                      if (e.target.value === 'none') {
                        handleChange('last_diploma_month', '')
                        handleChange('last_diploma_year', '')
                      }
                      handleChange('last_diploma_name', e.target.value)
                    }}
                    value={form.data.last_diploma_name}
                  >
                    {form.diplomas.map((diploma) => (
                      <FixedMenuItem key={diploma.id} value={diploma.id}>
                        {diploma.name}
                      </FixedMenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Grid container alignItems="center">
            <Checkbox
              id="authorized_contact_with_companies"
              sx={{ ml: -1 }}
              checked={form.data.authorized_contact_with_companies ?? false}
              onClick={() => {
                handleChange('authorized_contact_with_companies', !form.data.authorized_contact_with_companies)
              }}
            />
            <Typography maxWidth={700}>
              En remplissant ce questionnaire, j'autorise Walter Learning à contacter des entreprises en mon nom dans le
              cadre de mon projet d'alternance.
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid
        item
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          mt: 4,
        }}
      >
        <Button
          variant="outlined"
          sx={{
            height: {
              xs: 45,
            },
          }}
          startIcon={<MdNavigateBefore />}
          onClick={() => {
            navigate({ pathname: './../profile/', search: location.search })
          }}
        >
          Précédent
        </Button>

        <Button
          variant="contained"
          sx={{
            height: {
              xs: 45,
            },
          }}
          disabled={!isApprenticeshipStepValid('situation', form)}
          endIcon={<MdNavigateNext />}
          onClick={submitInfo}
        >
          Valider
        </Button>
      </Grid>
    </Layout>
  )
}

export default Situation
